import * as Api from '@ViewModels';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	ClientSite,
	DraftPreviewPage,
	SitemapNode,
	SitePage,
	SitePendingChanges,
	WebsiteAnalyticsDailyReport,
} from '../../extViewmodels/sdk/models/Website';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../web/ReactQueryProvider';

// The account's own Levitate site, as the site editor sees it. All of these run under the caller's own context;
// the API resolves permission into `access` so nothing here reasons about it.

const SITE_QUERY_KEY = `/site`;
const SITEMAP_QUERY_KEY = `/site/sitemap`;
const SITE_PAGE_QUERY_KEY = `/site/page`;
const PENDING_CHANGES_QUERY_KEY = `/site/pendingChanges`;
const DRAFT_PREVIEW_PAGE_QUERY_KEY = `/site/draftPreview/page`;
const DAILY_ANALYTICS_QUERY_KEY = `/site/analytics/daily`;

export function useClientSiteQuery({
	enabled = true,
	impersonationContext,
	toastOnError = true,
}: {
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	toastOnError?: boolean;
} = {}) {
	const userSession = useUserSession();
	return useQuery<ClientSite>({
		enabled,
		queryKey: [SITE_QUERY_KEY, impersonationContext],
		queryFn: () =>
			userSession.webServiceHelper.callAsync(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `site`, impersonationContext }),
				'GET'
			),
		meta: toastOnError ? { toastErrorMessage: 'Failed to load your website.' } : undefined,
	});
}

export function useClientSitemapQuery({
	enabled = true,
	impersonationContext,
}: {
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
} = {}) {
	const userSession = useUserSession();
	return useQuery<SitemapNode[]>({
		enabled,
		queryKey: [SITEMAP_QUERY_KEY, impersonationContext],
		queryFn: () =>
			userSession.webServiceHelper.callAsync(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `site/sitemap`, impersonationContext }),
				'GET'
			),
		meta: {
			toastErrorMessage: 'Failed to load your site’s pages.',
		},
	});
}

export function useClientSitePendingChangesQuery({
	enabled = true,
	impersonationContext,
}: {
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
} = {}) {
	const userSession = useUserSession();
	return useQuery<SitePendingChanges>({
		enabled,
		queryKey: [PENDING_CHANGES_QUERY_KEY, impersonationContext],
		queryFn: () =>
			userSession.webServiceHelper.callAsync(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `site/pendingChanges`, impersonationContext }),
				'GET'
			),
		meta: {
			toastErrorMessage: 'Failed to check for unpublished changes.',
		},
	});
}

export function useClientSitePageQuery({
	pageId,
	enabled = true,
	impersonationContext,
}: {
	pageId?: string;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const userSession = useUserSession();
	return useQuery<SitePage>({
		enabled: enabled && Boolean(pageId),
		queryKey: [SITE_PAGE_QUERY_KEY, pageId, impersonationContext],
		queryFn: () =>
			userSession.webServiceHelper.callAsync(
				Api.ImpersonationBroker.composeApiUrl({ urlPath: `site/page/${pageId}`, impersonationContext }),
				'GET'
			),
		meta: {
			toastErrorMessage: 'Failed to load this page, so its pictures can’t be changed right now.',
		},
	});
}

export type SitePageImageEdit =
	| { kind: 'replaceImage'; elementIndex: number; fieldPath: string; fileAttachmentId: string; alt?: string }
	| { kind: 'addSlide'; elementIndex: number; position: number; fileAttachmentId: string; alt?: string }
	| { kind: 'removeSlide'; elementIndex: number; slideIndex: number }
	| { kind: 'moveSlide'; elementIndex: number; slideIndex: number; toIndex: number };

/** Every write is guarded by the page's `lastModifiedDate`, so a page that changed since it loaded gets a 409. */
export function useSitePageImageEditMutation({
	onSuccess,
	onError,
	impersonationContext,
}: {
	onSuccess: (page: SitePage, edit: SitePageImageEdit) => void;
	onError: (error: Api.IOperationResultNoValue, edit: SitePageImageEdit) => void;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: ({ page, edit }: { page: SitePage; edit: SitePageImageEdit }) => {
			const ifLastModifiedBefore = page.lastModifiedDate;
			const elementPath = `site/page/${page.id}/element/${edit.elementIndex}`;
			const request = (() => {
				switch (edit.kind) {
					case 'replaceImage':
						return {
							urlPath: `${elementPath}/image`,
							method: 'PUT' as const,
							body: {
								fieldPath: edit.fieldPath,
								fileAttachmentId: edit.fileAttachmentId,
								alt: edit.alt,
								ifLastModifiedBefore,
							},
						};
					case 'addSlide':
						return {
							urlPath: `${elementPath}/slides`,
							method: 'POST' as const,
							body: {
								position: edit.position,
								fileAttachmentId: edit.fileAttachmentId,
								alt: edit.alt,
								ifLastModifiedBefore,
							},
						};
					case 'removeSlide':
						return {
							urlPath: `${elementPath}/slides/${edit.slideIndex}`,
							method: 'DELETE' as const,
							queryParams: { ifLastModifiedBefore },
						};
					default:
						return {
							urlPath: `${elementPath}/slides/${edit.slideIndex}/move`,
							method: 'POST' as const,
							body: { toIndex: edit.toIndex, ifLastModifiedBefore },
						};
				}
			})();
			return userSession.webServiceHelper.callAsync<SitePage>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: request.urlPath,
					queryParams: 'queryParams' in request ? request.queryParams : undefined,
					impersonationContext,
				}),
				request.method,
				'body' in request ? request.body : undefined
			);
		},
		onSuccess: (updated, { edit }) => onSuccess(updated, edit),
		onError: (error: Api.IOperationResultNoValue, { edit }) => onError(error, edit),
	});
}

/**
 * One page of the unpublished site as a self-contained document, fetched under the app's own bearer token and shown
 * in the preview frame as `srcdoc`. Bump `reloadKey` after an agent change to refetch the same path.
 */
export function useDraftPreviewPageQuery({
	pagePath,
	reloadKey,
	enabled = true,
	impersonationContext,
}: {
	pagePath: string;
	reloadKey: number;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const userSession = useUserSession();
	return useQuery<DraftPreviewPage>({
		enabled,
		queryKey: [DRAFT_PREVIEW_PAGE_QUERY_KEY, pagePath, reloadKey, impersonationContext],
		queryFn: () =>
			userSession.webServiceHelper.callAsync(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `site/draftPreview/page`,
					queryParams: { path: pagePath },
					impersonationContext,
				}),
				'GET'
			),
		placeholderData: previous => previous,
		meta: {
			toastErrorMessage: 'Failed to load the page preview.',
		},
	});
}

/** The site's traffic over an inclusive range of `yyyy-MM-dd` days, from the nightly rollups. */
export function useClientSiteDailyAnalyticsQuery({
	from,
	to,
	enabled = true,
	impersonationContext,
}: {
	from: string;
	to: string;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const userSession = useUserSession();
	return useQuery<WebsiteAnalyticsDailyReport>({
		enabled,
		queryKey: [DAILY_ANALYTICS_QUERY_KEY, from, to, impersonationContext],
		queryFn: () =>
			userSession.webServiceHelper.callAsync(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `site/analytics/daily`,
					queryParams: { from, to },
					impersonationContext,
				}),
				'GET'
			),
		placeholderData: previous => previous,
		meta: {
			toastErrorMessage: 'Failed to load your website’s traffic.',
		},
	});
}

export const invalidateClientSiteQueries = () => {
	reactQueryClient.invalidateQueries({ queryKey: [SITE_QUERY_KEY] });
	reactQueryClient.invalidateQueries({ queryKey: [SITEMAP_QUERY_KEY] });
	reactQueryClient.invalidateQueries({ queryKey: [SITE_PAGE_QUERY_KEY] });
	reactQueryClient.invalidateQueries({ queryKey: [PENDING_CHANGES_QUERY_KEY] });
	reactQueryClient.invalidateQueries({ queryKey: [DRAFT_PREVIEW_PAGE_QUERY_KEY] });
};
